import Decimal from 'decimal.js';

/**
 * Decimal.js utilities for precise financial calculations
 * Avoids floating-point precision errors in monetary calculations
 */

// Configure Decimal.js for financial calculations
Decimal.set({ 
  precision: 20,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -9,
  toExpPos: 9
});

/**
 * Decimal utility class for precise financial calculations
 */
export class DecimalCalc {
  private value: Decimal;

  constructor(value: number | string | Decimal) {
    this.value = new Decimal(value || 0);
  }

  add(other: number | string | Decimal | DecimalCalc): DecimalCalc {
    const otherValue = other instanceof DecimalCalc ? other.value : new Decimal(other || 0);
    return new DecimalCalc(this.value.add(otherValue));
  }

  sub(other: number | string | Decimal | DecimalCalc): DecimalCalc {
    const otherValue = other instanceof DecimalCalc ? other.value : new Decimal(other || 0);
    return new DecimalCalc(this.value.sub(otherValue));
  }

  mul(other: number | string | Decimal | DecimalCalc): DecimalCalc {
    const otherValue = other instanceof DecimalCalc ? other.value : new Decimal(other || 0);
    return new DecimalCalc(this.value.mul(otherValue));
  }

  div(other: number | string | Decimal | DecimalCalc): DecimalCalc {
    const otherValue = other instanceof DecimalCalc ? other.value : new Decimal(other || 0);
    if (otherValue.isZero()) {
      return new DecimalCalc(0);
    }
    return new DecimalCalc(this.value.div(otherValue));
  }

  toNumber(): number {
    return this.value.toNumber();
  }

  toString(): string {
    return this.value.toString();
  }

  static round(value: number | string | Decimal | DecimalCalc, decimalPlaces: number = 2): DecimalCalc {
    const decimalValue = value instanceof DecimalCalc ? value.value : new Decimal(value || 0);
    return new DecimalCalc(decimalValue.toDecimalPlaces(decimalPlaces));
  }

  toDecimalPlaces(decimalPlaces: number = 2): DecimalCalc {
    return new DecimalCalc(this.value.toDecimalPlaces(decimalPlaces));
  }

  lessThan(other: number | string | Decimal | DecimalCalc): boolean {
    const otherValue = other instanceof DecimalCalc ? other.value : new Decimal(other || 0);
    return this.value.lessThan(otherValue);
  }

  equals(other: number | string | Decimal | DecimalCalc): boolean {
    const otherValue = other instanceof DecimalCalc ? other.value : new Decimal(other || 0);
    return this.value.equals(otherValue);
  }

  static sum(values: (number | string | Decimal | DecimalCalc)[]): DecimalCalc {
    return values.reduce<DecimalCalc>(
      (acc, val) => acc.add(val),
      new DecimalCalc(0)
    );
  }

  static min(values: (number | string | Decimal | DecimalCalc)[]): DecimalCalc {
    const decimalValues = values.map(v => 
      v instanceof DecimalCalc ? v.value : new Decimal(v || 0)
    );
    return new DecimalCalc(Decimal.min(...decimalValues));
  }

  static max(values: (number | string | Decimal | DecimalCalc)[]): DecimalCalc {
    const decimalValues = values.map(v => 
      v instanceof DecimalCalc ? v.value : new Decimal(v || 0)
    );
    return new DecimalCalc(Decimal.max(...decimalValues));
  }

  static percentage(value: number | string | Decimal | DecimalCalc, percent: number | string | Decimal | DecimalCalc): DecimalCalc {
    const decimalValue = value instanceof DecimalCalc ? value.value : new Decimal(value || 0);
    const decimalPercent = percent instanceof DecimalCalc ? percent.value : new Decimal(percent || 0);
    return new DecimalCalc(decimalValue.times(decimalPercent).div(100));
  }

  /**
   * Proportionally allocates a total discount (in ₹) across line items based on each item's
   * value (sellingPrice × quantity). Rounds each share down to 2 decimal places, then
   * distributes the rounding remainder penny-by-penny to items with the highest total value.
   * This ensures the allocated amounts always sum exactly to the total discount.
   */
  static allocateDiscount(
    items: { price: { sellingPrice: number }; quantity: number }[],
    totalDiscount: number
  ): number[] {
    if (totalDiscount <= 0 || items.length === 0) {
      return items.map(() => 0);
    }

    // Calculate the total value (sellingPrice * quantity) for each item
    const itemTotalValues = items.map(
      item => new Decimal(item.price?.sellingPrice || 0).times(item.quantity)
    );
    const subTotal = DecimalCalc.sum(itemTotalValues);

    if (subTotal.equals(0)) {
      return items.map(() => 0);
    }

    // 1. Calculate proportional shares and round down to 2 decimal places
    const roundedDiscounts = items.map((_item, idx) => {
      const share = itemTotalValues[idx].div(subTotal.value).times(totalDiscount);
      return share.toDecimalPlaces(2, Decimal.ROUND_DOWN);
    });

    // 2. Find the rounding remainder (e.g., 10.00 - 9.99 = 0.01)
    const sumOfRounded = roundedDiscounts.reduce(
      (acc, val) => acc.plus(val),
      new Decimal(0)
    );
    let remainder = new Decimal(totalDiscount).minus(sumOfRounded);

    // 3. Sort indexes by total item value descending (largest value gets the penny first)
    const sortedIndexes = items
      .map((_, idx) => idx)
      .sort((a, b) => {
        const valA = itemTotalValues[a];
        const valB = itemTotalValues[b];
        if (valA.equals(valB)) {
          return a - b; // Keep it deterministic if values are identical
        }
        return valB.comparedTo(valA);
      });

    // 4. Distribute the remainder penny-by-penny
    let index = 0;
    while (remainder.greaterThanOrEqualTo(0.01) && index < sortedIndexes.length) {
      const targetIdx = sortedIndexes[index];
      roundedDiscounts[targetIdx] = roundedDiscounts[targetIdx].plus(0.01);
      remainder = remainder.minus(0.01);
      index = (index + 1) % sortedIndexes.length;
    }

    return roundedDiscounts.map(d => d.toNumber());
  }
}

/**
 * Helper function to create a DecimalCalc instance
 */
export const decimal = (value: number | string | Decimal = 0): DecimalCalc => {
  return new DecimalCalc(value);
};